import React, {
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDF_FONT, registerPdfFont } from "../../../utils/pdfFont";
import {
  submitSiboForm,
  markTestAsProcessing,
  fetchTherapistForClient,
} from "../../../Services/apiService";
import { cardTileSx, cardAddTileSx, cardRemoveButtonSx } from "../../../theme/cardTileTokens";

// Ported from src/pages/AnalysisDetails.js. Renders the read-only/editable
// Information Form. formData is now owned by the parent workspace (fetched
// once by useWorkspaceData) instead of being fetched locally, so this tab
// works correctly no matter which tab is active on mount.
const DetailsTab = forwardRef(function DetailsTab(
  {
    formData,
    setFormData,
    orderId,
    testId,
    loading,
    error,
    onRetry,
    onEditingStateChange,
    onNotify,
  },
  ref
) {
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (err) {
      console.error("Date formatting error:", err);
      return dateString;
    }
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editedFormData, setEditedFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Therapist search state
  const [showTherapistSuggestions, setShowTherapistSuggestions] = useState(false);
  const [filteredTherapists, setFilteredTherapists] = useState([]);
  const [therapistOptions, setTherapistOptions] = useState([]);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTherapists, setHasMoreTherapists] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  const loadMoreTherapists = useCallback(
    async (searchQuery = "") => {
      if (!hasMoreTherapists || isSearching) return [];

      try {
        setIsSearching(true);
        const response = await fetchTherapistForClient(currentPage, 10000);

        if (response && response.therapists) {
          const newTherapists = response.therapists.map((therapist) => ({
            id: therapist.id,
            name: therapist.therapist_name,
            practice: therapist.practice_name,
            city: therapist.city,
            email: therapist.therapist_email,
          }));

          setTherapistOptions((prev) => [...prev, ...newTherapists]);
          setCurrentPage((prev) => prev + 1);

          if (!response.hasMore || newTherapists.length === 0) {
            setHasMoreTherapists(false);
          }

          if (searchQuery) {
            const query = (searchQuery ?? "").toString().toLowerCase();
            return newTherapists.filter((therapist) => {
              const name = (therapist.name ?? "").toString().toLowerCase();
              const practice = (therapist.practice ?? "").toString().toLowerCase();
              const city = (therapist.city ?? "").toString().toLowerCase();
              return name.includes(query) || practice.includes(query) || city.includes(query);
            });
          }

          return newTherapists;
        }
        return [];
      } catch (err) {
        console.error("Error loading therapists:", err);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [hasMoreTherapists, isSearching, currentPage]
  );

  // Update form field helper
  const updateFormField = (path, value) => {
    setEditedFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleTherapistSearch = async (value) => {
    updateFormField("therapistSearch", value);

    if (loadingTherapists) return;

    if (value && value.length > 0) {
      const query = (value ?? "").toString().toLowerCase();

      let filtered = therapistOptions.filter((therapist) => {
        const name = (therapist.name ?? "").toString().toLowerCase();
        const practice = (therapist.practice ?? "").toString().toLowerCase();
        const city = (therapist.city ?? "").toString().toLowerCase();
        return name.includes(query) || practice.includes(query) || city.includes(query);
      });

      if (filtered.length === 0 && hasMoreTherapists && !isSearching) {
        const newMatches = await loadMoreTherapists(value);
        filtered = [...filtered, ...newMatches];
      }

      setFilteredTherapists(filtered);
      setShowTherapistSuggestions(true);
    } else {
      setFilteredTherapists([]);
      setShowTherapistSuggestions(false);
    }
  };

  const handleTherapistSelection = (therapist) => {
    setEditedFormData((prev) => ({
      ...prev,
      therapistSearch: `${therapist.name} - ${therapist.practice}, ${therapist.city}`,
      selectedTherapist: therapist.id,
      otherTherapist: false,
      therapist: {
        therapist_name: therapist.name,
        practice_name: therapist.practice,
        city: therapist.city,
        email: therapist.email,
      },
    }));
    setShowTherapistSuggestions(false);
  };

  const handleCustomTherapist = () => {
    setEditedFormData((prev) => ({
      ...prev,
      therapistSearch: "Other (not listed)",
      selectedTherapist: "other",
      otherTherapist: true,
    }));
    setShowTherapistSuggestions(false);
  };

  useEffect(() => {
    if (isEditing && therapistOptions.length === 0) {
      setLoadingTherapists(true);
      loadMoreTherapists().finally(() => setLoadingTherapists(false));
    }
  }, [isEditing, therapistOptions.length, loadMoreTherapists]);

  const handleDownloadForm = async () => {
    if (!formData) {
      console.error("No form data available for PDF generation");
      return;
    }

    try {
      await markTestAsProcessing([{ orderId, testId }]);
    } catch (err) {
      console.error("❌ Error marking test as processing:", err);
    }

    const doc = new jsPDF();
    await registerPdfFont(doc);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(18);
    doc.setFont(PDF_FONT, "bold");
    const title = "Sibolab Auswertung";
    const pageWidth = doc.internal.pageSize.width;
    const titleWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(title, titleX, 20);

    const currentDate = formatDateForDisplay(new Date().toISOString().split("T")[0]);

    const dateObj = new Date(formData?.submissionTimestamp);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    const infoFields = [
      ["Order Nummer:", formData.test_information?.order_number || ""],
      ["Test ID:", formData.test_information?.test_identifier || testId || ""],
      ["Geburtsdatum:", formatDateForDisplay(formData.mandatory_information?.date_of_birth)],
      ["Datum Probeentnahme:", formattedDate],
      ["Datum Auswertung:", currentDate],
      ["Substrat:", formData.test_information?.substrate || ""],
    ];

    const infoTableStartY = 35;
    autoTable(doc, {
      body: infoFields,
      startY: infoTableStartY,
      margin: { left: 15, right: 15 },
      theme: "grid",
      styles: {
        font: PDF_FONT,
        fontSize: 11,
        cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        valign: "middle",
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold" },
        1: { cellWidth: pageWidth - 80 },
      },
    });

    const tableColumns = ["Probenummer", "H2 (ppm)", "CH4 (ppm)", "CO2 (%)"];
    const tableRows = Array.from({ length: 10 }, (_, i) => [(i + 1).toString(), "", "", ""]);

    const analysisTableStartY = doc.lastAutoTable.finalY + 10;
    const usableWidth = pageWidth - 30;
    const columnWidth = usableWidth / tableColumns.length;

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: analysisTableStartY,
      margin: { left: 15, right: 15 },
      styles: {
        font: PDF_FONT,
        fontSize: 16,
        cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        textColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        font: PDF_FONT,
        fontStyle: "bold",
        lineWidth: 0.4,
      },
      columnStyles: {
        0: { cellWidth: columnWidth },
        1: { cellWidth: columnWidth },
        2: { cellWidth: columnWidth },
        3: { cellWidth: columnWidth },
      },
      theme: "grid",
      pageBreak: "avoid",
    });

    const notesY = doc.lastAutoTable.finalY + 15;
    const notesHeight = 30;
    const notesMargin = 15;
    const notesWidth = pageWidth - notesMargin * 2;

    doc.setDrawColor(0, 0, 0);
    doc.rect(notesMargin, notesY, notesWidth, notesHeight);

    doc.setFont(PDF_FONT, "bold");
    doc.setFontSize(16);
    doc.text("Notizen:", notesMargin + 2, notesY - 2);

    doc.save(`Sibolab_Auswertung_${formData.test_information?.order_number || orderId}.pdf`);
  };

  const handleEditForm = () => {
    if (formData) {
      const editedData = JSON.parse(JSON.stringify(formData));

      if (!editedData.therapistSearch) {
        if (editedData.therapist?.therapist_name) {
          editedData.therapistSearch = `${editedData.therapist.therapist_name} - ${editedData.therapist.practice_name || ""}, ${editedData.therapist.city || ""}`;
          editedData.selectedTherapist = editedData.therapist?.therapist_id || "existing";
          editedData.otherTherapist = editedData.therapist?.other || false;
        } else {
          editedData.therapistSearch = "";
          editedData.selectedTherapist = "";
          editedData.otherTherapist = false;
        }
      }

      if (!editedData.additional_email) {
        editedData.additional_email = { emails: [] };
      } else if (!editedData.additional_email.emails) {
        editedData.additional_email = {
          emails: editedData.additional_email.email ? [editedData.additional_email.email] : [],
        };
      }

      setEditedFormData(editedData);
    } else {
      setEditedFormData({
        mandatory_information: { first_name: "", last_name: "", date_of_birth: "" },
        additional_information: {
          InitialTest: { InitialFindings: false, followup_test: false },
          dailySymptoms: {
            constipation: false,
            bloating: false,
            diarrhoea: false,
            abdominalPain: false,
            mixed: false,
            flatulence: false,
          },
        },
        therapist: { therapist_name: "", practice_name: "", city: "", email: "" },
        additional_email: { emails: [] },
        therapistSearch: "",
        selectedTherapist: "",
        otherTherapist: false,
        testSymptoms: {
          bloating: { checked: false, minutes: "" },
          diarrhoea: { checked: false, minutes: "" },
          abdominalPain: { checked: false, minutes: "" },
          others: { checked: false, symptoms: [{ description: "", minutes: "", wordCount: 0 }] },
        },
        test_information: formData?.test_information || {
          order_number: orderId,
          test_identifier: testId,
        },
      });
    }
    setIsEditing(true);
  };

  const handleTestSymptomChange = (symptomType, field, value) => {
    setEditedFormData((prev) => ({
      ...prev,
      testSymptoms: {
        ...prev.testSymptoms,
        [symptomType]: { ...prev.testSymptoms[symptomType], [field]: value },
      },
    }));
  };

  const handleOthersDescriptionChange = (index, value) => {
    const wordCount = value.trim().split(/\s+/).filter((word) => word.length > 0).length;
    setEditedFormData((prev) => ({
      ...prev,
      testSymptoms: {
        ...prev.testSymptoms,
        others: {
          ...prev.testSymptoms.others,
          symptoms: prev.testSymptoms.others.symptoms.map((symptom, i) =>
            i === index ? { ...symptom, description: value, wordCount } : symptom
          ),
        },
      },
    }));
  };

  const handleOthersMinutesChange = (index, value) => {
    setEditedFormData((prev) => ({
      ...prev,
      testSymptoms: {
        ...prev.testSymptoms,
        others: {
          ...prev.testSymptoms.others,
          symptoms: prev.testSymptoms.others.symptoms.map((symptom, i) =>
            i === index ? { ...symptom, minutes: value } : symptom
          ),
        },
      },
    }));
  };

  const handleSymptomCardClick = (key) => {
    if (!isEditing) return;
    const nextChecked = !(editedFormData?.testSymptoms?.[key]?.checked || false);
    handleTestSymptomChange(key, "checked", nextChecked);
    if (key === "others" && nextChecked && (editedFormData?.testSymptoms?.others?.symptoms || []).length === 0) {
      addOtherDescription();
    }
  };

  const addOtherDescription = () => {
    setEditedFormData((prev) => ({
      ...prev,
      testSymptoms: {
        ...prev.testSymptoms,
        others: {
          ...prev.testSymptoms.others,
          symptoms: [
            ...prev.testSymptoms.others.symptoms,
            { description: "", minutes: "", wordCount: 0 },
          ],
        },
      },
    }));
  };

  const removeOtherDescription = (index) => {
    setEditedFormData((prev) => ({
      ...prev,
      testSymptoms: {
        ...prev.testSymptoms,
        others: {
          ...prev.testSymptoms.others,
          symptoms: prev.testSymptoms.others.symptoms.filter((_, i) => i !== index),
        },
      },
    }));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedFormData(null);
  };

  const handleSaveForm = async () => {
    if (!editedFormData) return;

    try {
      setSaving(true);

      const dataToSave = {
        test_information: {
          order_number: parseInt(orderId),
          test_identifier: testId,
          title: editedFormData.test_information?.title,
          substrate: editedFormData.test_information?.substrate,
          lab_test_id: editedFormData.test_information?.lab_test_id || testId,
        },
        mandatory_information: {
          first_name: editedFormData.mandatory_information?.first_name || "",
          last_name: editedFormData.mandatory_information?.last_name || "",
          date_of_birth: editedFormData.mandatory_information?.date_of_birth || "",
        },
        additional_information: {
          InitialTest: {
            InitialFindings:
              editedFormData.additional_information?.InitialTest?.InitialFindings || false,
            followup_test:
              editedFormData.additional_information?.InitialTest?.followup_test || false,
          },
          dailySymptoms: {
            constipation:
              editedFormData.additional_information?.dailySymptoms?.constipation || false,
            bloating: editedFormData.additional_information?.dailySymptoms?.bloating || false,
            diarrhoea: editedFormData.additional_information?.dailySymptoms?.diarrhoea || false,
            abdominalPain:
              editedFormData.additional_information?.dailySymptoms?.abdominalPain || false,
            mixed: editedFormData.additional_information?.dailySymptoms?.mixed || false,
            flatulence:
              editedFormData.additional_information?.dailySymptoms?.flatulence || false,
          },
        },
        therapist: {
          other:
            editedFormData.otherTherapist || editedFormData.selectedTherapist === "other",
          therapist_id:
            editedFormData.otherTherapist || editedFormData.selectedTherapist === "other"
              ? null
              : parseInt(editedFormData.selectedTherapist) || null,
          therapist_name: editedFormData.therapist?.therapist_name || "",
          practice_name: editedFormData.therapist?.practice_name || "",
          city: editedFormData.therapist?.city || "",
          email: editedFormData.therapist?.email || "",
        },
        additional_email: { emails: editedFormData.additional_email?.emails || [] },
        testSymptoms: {
          bloating: {
            checked: editedFormData.testSymptoms?.bloating?.checked || false,
            minutes: editedFormData.testSymptoms?.bloating?.minutes || "",
          },
          diarrhoea: {
            checked: editedFormData.testSymptoms?.diarrhoea?.checked || false,
            minutes: editedFormData.testSymptoms?.diarrhoea?.minutes || "",
          },
          abdominalPain: {
            checked: editedFormData.testSymptoms?.abdominalPain?.checked || false,
            minutes: editedFormData.testSymptoms?.abdominalPain?.minutes || "",
          },
          others: {
            checked: editedFormData.testSymptoms?.others?.checked || false,
            symptoms: editedFormData.testSymptoms?.others?.symptoms || [],
          },
        },
        consentDeclaration: true,
        dataUsageAgreement: true,
        submissionTimestamp: editedFormData?.submissionTimestamp,
        formVersion: "1.0",
      };

      await submitSiboForm(dataToSave);

      setFormData(editedFormData);
      setIsEditing(false);
      setEditedFormData(null);

      onNotify?.("Form saved successfully", "success");
    } catch (err) {
      console.error("❌ Error saving form:", err);
      onNotify?.("Error saving form: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    startEdit: handleEditForm,
    save: handleSaveForm,
    cancel: handleCancelEdit,
    downloadForm: handleDownloadForm,
  }));

  useEffect(() => {
    onEditingStateChange?.(isEditing, saving);
  }, [isEditing, saving, onEditingStateChange]);

  const SectionHeading = ({ children }) => (
    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: "#000" }}>
      {children}
    </Typography>
  );

  const checkboxSx = {
    color: "#c7cacd",
    "&.Mui-checked": { color: "#16498A" },
    "&.Mui-disabled": { color: "#e4e5e7" },
  };


  return (
    <Box sx={{ width: "100%", maxWidth: "100vw", overflow: "hidden" }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading analysis details...</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ minHeight: "50vh", py: 4, px: 2 }}>
          <Box sx={{ maxWidth: "800px", mx: "auto" }}>
            <Card elevation={1} sx={{ backgroundColor: "#ffffff" }}>
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Alert severity="error" sx={{ mb: 3, display: "flex", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Typography variant="h6" sx={{ fontWeight: "500" }}>
                      Error Loading Analysis Details
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, textAlign: "left" }}>
                    {error}
                  </Typography>
                </Alert>
                <Button variant="contained" color="primary" onClick={onRetry} sx={{ mt: 2 }}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {!loading && !error && (formData || isEditing) && (
        <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
          <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Box
              sx={{
                borderRadius: "24px",
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(40,40,40,0.06)",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                p: { xs: 3, md: 5 },
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
            >
              {/* Test Information */}
              <SectionHeading>Test Information</SectionHeading>
              <Box sx={{ mb: 4 }}>
                {[
                  ["Order Number", formData?.test_information?.order_number],
                  ["Test Identifier", formData?.test_information?.test_identifier],
                  ["Test Type", formData?.test_information?.title],
                  ["Test Substrate", formData?.test_information?.substrate],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #f0f0f0",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                      {value || "---"}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

              {/* Mandatory Information */}
              <SectionHeading>Mandatory Information</SectionHeading>
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={
                        isEditing
                          ? editedFormData?.mandatory_information?.first_name || ""
                          : formData?.mandatory_information?.first_name || ""
                      }
                      onChange={(e) =>
                        isEditing &&
                        updateFormField("mandatory_information.first_name", e.target.value)
                      }
                      InputProps={{ readOnly: !isEditing }}
                      variant="outlined"
                      size="small"
                      required={isEditing}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={
                        isEditing
                          ? editedFormData?.mandatory_information?.last_name || ""
                          : formData?.mandatory_information?.last_name || ""
                      }
                      onChange={(e) =>
                        isEditing &&
                        updateFormField("mandatory_information.last_name", e.target.value)
                      }
                      InputProps={{ readOnly: !isEditing }}
                      variant="outlined"
                      size="small"
                      required={isEditing}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Date of Birth"
                        value={
                          isEditing
                            ? editedFormData?.mandatory_information?.date_of_birth
                              ? dayjs(editedFormData.mandatory_information.date_of_birth)
                              : null
                            : formData?.mandatory_information?.date_of_birth
                              ? dayjs(formData.mandatory_information.date_of_birth)
                              : null
                        }
                        format="DD/MM/YYYY"
                        onChange={(newValue) => {
                          if (isEditing) {
                            updateFormField(
                              "mandatory_information.date_of_birth",
                              newValue ? newValue.format("YYYY-MM-DD") : ""
                            );
                          }
                        }}
                        maxDate={dayjs()}
                        readOnly={!isEditing}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            variant: "outlined",
                            required: isEditing,
                            InputLabelProps: { shrink: true },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

              {/* Additional Information */}
              <SectionHeading>Additional Information</SectionHeading>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#555" }}>
                  Test Type
                </Typography>
                <FormGroup row sx={{ mb: 2.5, gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={checkboxSx}
                        checked={
                          isEditing
                            ? editedFormData?.additional_information?.InitialTest?.InitialFindings || false
                            : formData?.additional_information?.InitialTest?.InitialFindings || false
                        }
                        onChange={(e) =>
                          isEditing &&
                          updateFormField(
                            "additional_information.InitialTest.InitialFindings",
                            e.target.checked
                          )
                        }
                        disabled={!isEditing}
                      />
                    }
                    label="Initial Test"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={checkboxSx}
                        checked={
                          isEditing
                            ? editedFormData?.additional_information?.InitialTest?.followup_test || false
                            : formData?.additional_information?.InitialTest?.followup_test || false
                        }
                        onChange={(e) =>
                          isEditing &&
                          updateFormField(
                            "additional_information.InitialTest.followup_test",
                            e.target.checked
                          )
                        }
                        disabled={!isEditing}
                      />
                    }
                    label="Follow-up Test"
                  />
                </FormGroup>

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#555" }}>
                  Daily Symptoms
                </Typography>
                <FormGroup>
                  <Grid container spacing={0.5}>
                    {[
                      ["constipation", "Constipation"],
                      ["bloating", "Bloating"],
                      ["diarrhoea", "Diarrhoea"],
                      ["abdominalPain", "Abdominal Pain"],
                      ["mixed", "Mixed"],
                      ["flatulence", "Flatulence"],
                    ].map(([key, label]) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              sx={checkboxSx}
                              checked={
                                isEditing
                                  ? editedFormData?.additional_information?.dailySymptoms?.[key] || false
                                  : formData?.additional_information?.dailySymptoms?.[key] || false
                              }
                              onChange={(e) =>
                                isEditing &&
                                updateFormField(
                                  `additional_information.dailySymptoms.${key}`,
                                  e.target.checked
                                )
                              }
                              disabled={!isEditing}
                            />
                          }
                          label={label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Box>

              <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

              {/* Physician or Therapist */}
              <SectionHeading>Physician or Therapist</SectionHeading>
              <Box sx={{ mb: 4 }}>
                {isEditing && (
                  <ClickAwayListener onClickAway={() => setShowTherapistSuggestions(false)}>
                    <Box sx={{ position: "relative", mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search for therapist"
                        value={editedFormData?.therapistSearch || ""}
                        onChange={(e) => handleTherapistSearch(e.target.value)}
                        placeholder="Type therapist name, practice, or city..."
                        variant="outlined"
                        InputProps={{
                          endAdornment: loadingTherapists && <CircularProgress size={20} />,
                        }}
                      />
                      {showTherapistSuggestions && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            bgcolor: "white",
                            border: "1px solid #e0e0e0",
                            borderRadius: "12px",
                            maxHeight: 200,
                            overflow: "auto",
                            zIndex: 1000,
                            boxShadow: "0px 4px 12px rgba(16,24,40,0.10), 0px 2px 4px rgba(16,24,40,0.06)",
                            mt: 0.5,
                          }}
                        >
                          <List dense>
                            {filteredTherapists.map((therapist) => (
                              <ListItem
                                key={therapist.id}
                                button
                                onClick={() => handleTherapistSelection(therapist)}
                                sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}
                              >
                                <ListItemText
                                  primary={therapist.name}
                                  secondary={`${therapist.practice}, ${therapist.city}`}
                                />
                              </ListItem>
                            ))}
                            <ListItem
                              button
                              onClick={handleCustomTherapist}
                              sx={{ borderTop: "1px solid #eee", "&:hover": { bgcolor: "#f5f5f5" } }}
                            >
                              <ListItemText
                                primary="Other (not listed)"
                                secondary="Add custom therapist information"
                              />
                            </ListItem>
                          </List>
                        </Box>
                      )}
                    </Box>
                  </ClickAwayListener>
                )}

                <Box sx={{ mb: 2, p: 2, bgcolor: "#f8f9fa", borderRadius: "12px", border: "1px solid #e9ecef" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: "#6c757d" }}>
                    Therapist Type:{" "}
                    {(isEditing ? editedFormData?.otherTherapist : formData?.therapist?.other)
                      ? "Other (Custom Therapist)"
                      : "Selected from List"}
                  </Typography>
                  {(isEditing
                    ? editedFormData?.selectedTherapist && editedFormData?.selectedTherapist !== "other"
                    : formData?.therapist?.therapist_id) && (
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                      Therapist ID: {isEditing ? editedFormData?.selectedTherapist : formData?.therapist?.therapist_id}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Therapist Name"
                      value={
                        isEditing
                          ? editedFormData?.therapist?.therapist_name || ""
                          : formData?.therapist?.therapist_name || ""
                      }
                      onChange={(e) =>
                        isEditing && updateFormField("therapist.therapist_name", e.target.value)
                      }
                      InputProps={{ readOnly: !isEditing }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Practice Name"
                      value={
                        isEditing
                          ? editedFormData?.therapist?.practice_name || ""
                          : formData?.therapist?.practice_name || ""
                      }
                      onChange={(e) =>
                        isEditing && updateFormField("therapist.practice_name", e.target.value)
                      }
                      InputProps={{ readOnly: !isEditing }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={isEditing ? editedFormData?.therapist?.city || "" : formData?.therapist?.city || ""}
                      onChange={(e) => isEditing && updateFormField("therapist.city", e.target.value)}
                      InputProps={{ readOnly: !isEditing }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={isEditing ? editedFormData?.therapist?.email || "" : formData?.therapist?.email || ""}
                      onChange={(e) => isEditing && updateFormField("therapist.email", e.target.value)}
                      InputProps={{ readOnly: !isEditing }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

              {/* Additional email address */}
              <SectionHeading>Additional email address</SectionHeading>
              <Box sx={{ mb: 4 }}>
                {isEditing ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                      gap: 2,
                    }}
                  >
                    {(editedFormData?.additional_email?.emails || []).map((email, index) => (
                      <Box key={index} sx={cardTileSx}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newEmails = [...(editedFormData?.additional_email?.emails || [])];
                            newEmails.splice(index, 1);
                            updateFormField("additional_email.emails", newEmails);
                          }}
                          sx={cardRemoveButtonSx}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ color: "#9AA0A6", mb: 0.5 }}>
                          Email {index + 1}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const newEmails = [...(editedFormData?.additional_email?.emails || [])];
                            newEmails[index] = e.target.value;
                            updateFormField("additional_email.emails", newEmails);
                          }}
                          variant="standard"
                          placeholder="name@example.com"
                          InputProps={{ disableUnderline: true }}
                        />
                      </Box>
                    ))}
                    <Box
                      onClick={() => {
                        const currentEmails = editedFormData?.additional_email?.emails || [];
                        updateFormField("additional_email.emails", [...currentEmails, ""]);
                      }}
                      sx={cardAddTileSx}
                    >
                      <AddIcon />
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {(formData?.additional_email?.emails || []).length === 0 ? (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                        No additional email addresses
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                          gap: 2,
                        }}
                      >
                        {(formData?.additional_email?.emails || []).map((email, index) => (
                          <Box key={index} sx={cardTileSx}>
                            <Typography variant="caption" sx={{ color: "#9AA0A6", mb: 0.5 }}>
                              Email {index + 1}
                            </Typography>
                            <Typography sx={{ fontWeight: 500, wordBreak: "break-word" }}>{email}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

              {/* Occurrence of symptoms during test performance */}
              <SectionHeading>Occurrence of symptoms during test performance</SectionHeading>
              <Box sx={{ mb: 4 }}>
                {(() => {
                  const groupTall = ["bloating", "diarrhoea", "abdominalPain"].some((k) =>
                    isEditing
                      ? editedFormData?.testSymptoms?.[k]?.checked
                      : formData?.testSymptoms?.[k]?.checked
                  );

                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                        gap: 2,
                      }}
                    >
                      {[
                        ["bloating", "Bloating"],
                        ["diarrhoea", "Diarrhoea"],
                        ["abdominalPain", "Abdominal Pain"],
                        ["others", "Others"],
                      ].map(([key, label]) => {
                        const checked = isEditing
                          ? editedFormData?.testSymptoms?.[key]?.checked || false
                          : formData?.testSymptoms?.[key]?.checked || false;
                        const minutes = isEditing
                          ? editedFormData?.testSymptoms?.[key]?.minutes || ""
                          : formData?.testSymptoms?.[key]?.minutes || "";
                        const showMinutes = key !== "others" && checked;

                        return (
                          <Box
                            key={key}
                            onClick={() => handleSymptomCardClick(key)}
                            sx={{
                              borderRadius: "16px",
                              border: checked ? "2px solid #282828" : "1px dashed #c7cacd",
                              bgcolor: "#fff",
                              cursor: isEditing ? "pointer" : "default",
                              transition: "all 0.15s ease",
                              minHeight: groupTall ? 140 : 64,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              p: 2,
                              "&:hover": isEditing && !checked ? { borderColor: "#9AA0A6" } : {},
                            }}
                          >
                            <Typography
                              sx={{ fontWeight: checked ? 600 : 400, color: checked ? "#000" : "#9AA0A6" }}
                            >
                              {label}
                            </Typography>
                            {showMinutes && (
                              <Box sx={{ width: "100%" }} onClick={(e) => e.stopPropagation()}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Minutes after glucose"
                                  value={minutes}
                                  onChange={(e) =>
                                    isEditing && handleTestSymptomChange(key, "minutes", e.target.value)
                                  }
                                  InputProps={{ readOnly: !isEditing }}
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}

                {/* Other symptom descriptions — shown when the "Others" card is selected */}
                {(isEditing
                  ? editedFormData?.testSymptoms?.others?.checked
                  : formData?.testSymptoms?.others?.checked) && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                      gap: 2,
                    }}
                  >
                    {(isEditing
                      ? editedFormData?.testSymptoms?.others?.symptoms || []
                      : formData?.testSymptoms?.others?.symptoms || []
                    ).map((symptom, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                          borderRadius: "14px",
                          bgcolor: "#FAFAFA",
                          border: "1px solid #eee",
                          p: 2,
                          pt: isEditing ? 3.5 : 2,
                          minHeight: 176,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {isEditing && (
                          <IconButton
                            size="small"
                            onClick={() => removeOtherDescription(index)}
                            sx={cardRemoveButtonSx}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                        <TextField
                          fullWidth
                          label={`Symptom ${index + 1} Description`}
                          value={symptom.description || ""}
                          onChange={(e) =>
                            isEditing && handleOthersDescriptionChange(index, e.target.value)
                          }
                          InputProps={{ readOnly: !isEditing }}
                          variant="outlined"
                          size="small"
                          multiline
                          rows={3}
                          helperText={isEditing ? `Word count: ${symptom.wordCount || 0}` : ""}
                        />
                        <TextField
                          fullWidth
                          label="Minutes after glucose"
                          value={symptom.minutes || ""}
                          onChange={(e) =>
                            isEditing && handleOthersMinutesChange(index, e.target.value)
                          }
                          InputProps={{ readOnly: !isEditing }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    ))}
                    {isEditing && (
                      <Box onClick={addOtherDescription} sx={{ ...cardAddTileSx, minHeight: 176, height: "auto" }}>
                        <AddIcon />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4, borderColor: "#f0f0f0" }} />

              {/* Form Information */}
              <SectionHeading>Form Information</SectionHeading>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Submission Date"
                    type="datetime-local"
                    value={
                      isEditing
                        ? editedFormData?.submissionTimestamp
                          ? new Date(editedFormData.submissionTimestamp).toISOString().slice(0, 16)
                          : new Date().toISOString()
                        : formData?.submissionTimestamp
                          ? new Date(formData.submissionTimestamp).toISOString().slice(0, 16)
                          : ""
                    }
                    onChange={(e) => {
                      if (isEditing) {
                        const localValue = e.target.value;
                        const isoValue = localValue ? new Date(localValue).toISOString() : "";
                        updateFormField("submissionTimestamp", isoValue);
                      }
                    }}
                    InputProps={{ readOnly: !isEditing }}
                    variant="outlined"
                    size="small"
                    helperText={isEditing ? "Edit the date and time of submission" : ""}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default DetailsTab;
